import { useState, useRef, useEffect } from 'react';

interface VideoPlayerProps {
  thumbnail: string;
  title: string;
  duration: string;
}

export default function VideoPlayer({ thumbnail, title, duration }: VideoPlayerProps) {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (playing) {
      intervalRef.current = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 100) {
            setPlaying(false);
            return 0;
          }
          return prev + 0.4;
        });
      }, 200);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [playing]);

  const togglePlay = () => {
    if (playing) {
      setPlaying(false);
    } else {
      if (progress >= 100) setProgress(0);
      setPlaying(true);
    }
  };

  const formatTime = (pct: number) => {
    const [mins, secs] = duration.split(':').map(Number);
    const totalSecs = mins * 60 + secs;
    const currentSecs = Math.floor((pct / 100) * totalSecs);
    const m = Math.floor(currentSecs / 60);
    const s = currentSecs % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <div className="relative w-full rounded-3xl overflow-hidden bg-background-950 aspect-video group">
      <img
        src={thumbnail}
        alt={title}
        className={`w-full h-full object-cover object-top transition-all duration-500 ${
          playing ? 'opacity-0 scale-105' : 'opacity-100 scale-100'
        }`}
      />

      {!playing && (
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent flex items-center justify-center">
          <button
            onClick={togglePlay}
            className="w-20 h-20 md:w-24 md:h-24 flex items-center justify-center rounded-full bg-primary-500/90 hover:bg-primary-500 text-background-50 transition-all hover:scale-110 cursor-pointer shadow-2xl"
            aria-label="播放视频"
          >
            <i className="ri-play-fill text-3xl md:text-4xl ml-1"></i>
          </button>
        </div>
      )}

      {playing && (
        <div
          className="absolute inset-0 bg-background-950 cursor-pointer"
          onClick={togglePlay}
        >
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-primary-500/80 text-background-50 opacity-0 hover:opacity-100 transition-opacity">
              <i className="ri-pause-fill text-3xl"></i>
            </div>
          </div>
        </div>
      )}

      <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
        <div className="flex items-center gap-3 mb-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              togglePlay();
            }}
            className="w-8 h-8 flex items-center justify-center rounded-full text-background-50 hover:text-primary-400 transition-colors cursor-pointer"
            aria-label={playing ? '暂停' : '播放'}
          >
            <i className={`${playing ? 'ri-pause-fill' : 'ri-play-fill'} text-xl`}></i>
          </button>

          <div className="flex-1 h-1 bg-background-500/50 rounded-full overflow-hidden cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              const rect = e.currentTarget.getBoundingClientRect();
              const pct = ((e.clientX - rect.left) / rect.width) * 100;
              setProgress(Math.min(100, Math.max(0, pct)));
            }}
          >
            <div
              className="h-full bg-primary-500 rounded-full transition-all duration-200"
              style={{ width: `${progress}%` }}
            />
          </div>

          <span className="text-background-200 text-xs font-mono whitespace-nowrap">
            {formatTime(progress)} / {duration}
          </span>
        </div>
      </div>
    </div>
  );
}